/**
 * Consent-Mode-v2-Modul für Google Ads / GA4.
 *
 * Setzt Standard-Einwilligungen, bevor das Google-Tag lädt, und stellt
 * Hilfsfunktionen für das Cookie-Banner bereit.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const STORAGE_KEY = "cookie_consent";

/** Regionen, für die eine Einwilligung erforderlich ist (EEA, UK, CH, CA). */
const CONSENT_COUNTRIES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES",
  "SE", "IS", "LI", "NO", "GB", "CH", "CA",
];

let consentRegion: Promise<boolean> | undefined;

export function isConsentRequiredRegion(): Promise<boolean> {
  return (consentRegion ??= lookupConsentRegion());
}

async function lookupConsentRegion(): Promise<boolean> {
  try {
    const res = await fetch("/cdn-cgi/trace", { signal: AbortSignal.timeout(2000) });
    if (!res.ok) return true;
    const country = (await res.text()).match(/^loc=([A-Z0-9]{2})$/m)?.[1];
    if (!country || country === "XX" || country === "T1") return true;
    return CONSENT_COUNTRIES.includes(country);
  } catch {
    return true;
  }
}

/** Hat der Besucher ausdrücklich zugestimmt? */
export function hasAdConsent(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "granted";
}

/** Darf aktuell für Werbung getrackt werden? (Region + gespeicherte Wahl) */
export async function canTrackAds(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const choice = localStorage.getItem(STORAGE_KEY);
  if (choice !== null) return choice === "granted";
  const required = await isConsentRequiredRegion();
  const latest = localStorage.getItem(STORAGE_KEY);
  return latest !== null ? latest === "granted" : !required;
}

/** Consent-Status in Google aktualisieren. */
export function updateConsent(decision: "granted" | "denied") {
  if (typeof window === "undefined") return;
  if (decision === "denied") {
    window.gtag?.("set", "user_data", null);
  }
  window.gtag?.("consent", "update", {
    ad_storage: decision,
    ad_user_data: decision,
    ad_personalization: decision,
  });
}

/** Einwilligung speichern und an Google melden. */
export function acceptConsent() {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, "granted");
  updateConsent("granted");
}

/** Ablehnung speichern und an Google melden. */
export function rejectConsent() {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, "denied");
  updateConsent("denied");
}

/** Hat der Besucher bereits eine Wahl getroffen? */
export function hasConsentChoice(): boolean {
  if (typeof window === "undefined") return true;
  return localStorage.getItem(STORAGE_KEY) !== null;
}
